<template>
  <div class="container mx-auto px-4 py-8">
    <!-- Header -->
    <div class="hero bg-base-200 rounded-lg mb-8">
      <div class="hero-content text-center">
        <div class="max-w-md">
          <h1 class="text-5xl font-bold">Blog</h1>
          <p class="py-6">
            Descubre las últimas noticias y artículos sobre turismo y aventuras.
          </p>
        </div>
      </div>
    </div>

    <!-- Categories Filter -->
    <div class="flex flex-wrap gap-2 mb-8" v-if="categories?.length">
      <button
        @click="selectedCategory = null"
        :class="[
          'btn',
          selectedCategory === null ? 'btn-primary' : 'btn-outline',
        ]"
      >
        Todas
      </button>
      <button
        v-for="category in categories"
        :key="category.id"
        @click="selectedCategory = category.slug"
        :class="[
          'btn',
          selectedCategory === category.slug ? 'btn-primary' : 'btn-outline',
        ]"
      >
        {{ category.name }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="pending" class="flex justify-center py-8">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <!-- Articles Grid -->
    <div
      v-else-if="articles?.length"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
    >
      <BlogCard
        v-for="article in articles"
        :key="article.id"
        :article="article"
      />
    </div>

    <!-- No Articles -->
    <div v-else class="text-center py-8">
      <p class="text-lg text-base-content/70">No se encontraron artículos</p>
      <p class="text-sm text-base-content/50">
        {{
          selectedCategory
            ? "Prueba con otra categoría"
            : "Vuelve más tarde para ver nuevo contenido"
        }}
      </p>
    </div>

    <!-- Pagination -->
    <div v-if="pagination?.pageCount > 1" class="flex justify-center">
      <div class="join">
        <button
          v-for="page in paginationPages"
          :key="page"
          @click="currentPage = page"
          :class="[
            'join-item',
            'btn',
            currentPage === page ? 'btn-active' : '',
          ]"
        >
          {{ page }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
const { getArticles, getCategories } = useStrapi();

// Meta
useHead({
  title: "Blog - Soleando",
  meta: [
    {
      name: "description",
      content:
        "Descubre las últimas noticias y artículos sobre turismo y aventuras en nuestro blog.",
    },
  ],
});

// Reactive data
const selectedCategory = ref(null);
const currentPage = ref(1);
const pageSize = 9;

// Fetch categories
const { data: categoriesResponse } = await useAsyncData("categories", () =>
  getCategories()
);
const categories = computed(() => categoriesResponse.value?.data || []);

// Fetch articles (reactive to filters)
const {
  data: articlesResponse,
  pending,
  refresh,
} = await useAsyncData(
  "articles",
  () =>
    getArticles({
      page: currentPage.value,
      pageSize,
      category: selectedCategory.value,
    }),
  {
    watch: [selectedCategory, currentPage],
  }
);

const articles = computed(() => articlesResponse.value?.data || []);
const pagination = computed(
  () => articlesResponse.value?.meta?.pagination || {}
);

// Pagination helper
const paginationPages = computed(() => {
  const totalPages = pagination.value.pageCount || 1;
  const pages = [];

  for (let i = 1; i <= Math.min(totalPages, 5); i++) {
    pages.push(i);
  }

  return pages;
});

// Reset page when category changes
watch(selectedCategory, () => {
  currentPage.value = 1;
});
</script>

<style scoped>
/* Add any page-specific styles here */
</style>
